import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Home, Workflow, Users, BarChart3, Video, Music, Image,
  FileText, Podcast, HelpCircle, Shield,
  Megaphone, Target,
  BookOpen, LayoutDashboard, ClipboardList, Plug, Star,
  GraduationCap, Award, ChevronsLeft, ChevronsRight, Building2, Sparkles,
  Presentation, Mail, Zap, Globe, Link as LinkIcon, Kanban, Share2, Calendar,
  MessageCircle, Package, Smartphone, Settings, Layers, Tag, CreditCard, Newspaper,
  ArrowLeftRight,
} from 'lucide-react';
import { getAppName, isLyrraOne } from '@/lib/platform';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { useInstitutionAppTheme } from './InstitutionAppTheme';
import type { UserRole } from '@/types';
import { roleQualifiesForTrainerWebinarUi } from '@/lib/trainer-webinar-eligibility-ui';
import { roleQualifiesForEditorCreatorNav } from '@/lib/editor-role';
import { INSTITUTION_MENU_FLAGS, isInstitutionMenuEnabled } from '@/lib/institution-menu-flags';
import { LyrraOneLogo } from '@/components/brand/LyrraOneLogo';
import { rtlMirrorHorizontalChevron } from '@/lib/locale-direction';
import { buildFormateurOperationalNavItems, buildFormateurInstitutionNavItems } from '@/lib/formateur-institution-nav';
import { isMarketingPathAllowed, isInstitutionTabAllowed } from '@/lib/lyrraone-plan';
import { isValidInstitutionTab } from '@/lib/institution-tab-registry';
import { useLyrraOnePlan } from '@/hooks/useLyrraOnePlan';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { canAccessTrainerDashboard } from '@/lib/auth-role';
import { MOBILE_TRAINER_SALES_PATH } from '@/lib/mobile-workspace-access';
import { canAccessLoTeamPath, isLoTeamPermissionAllowed } from '@/lib/lyrraone-team-permissions';
import { setLoWorkspaceMode, type LoWorkspaceMode } from '@/lib/lyrraone-workspace-preference';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  /** Si défini : masqué lorsque l’admin plateforme a désactivé l’entrée pour l’établissement. */
  institutionMenuFlag?: string;
  requiresInstitution?: boolean;
}

interface NavSection {
  key: string;
  label: string;
  items: NavItem[];
}

const LYRRAONE_MARKETING_SECTION: NavSection = {
  key: 'marketing',
  label: 'nav.section.marketing',
  items: [
    { label: 'nav.marketingHub', path: '/marketing', icon: LayoutDashboard },
    { label: 'nav.contacts', path: '/marketing/contacts', icon: Users },
    { label: 'nav.emailMarketing', path: '/marketing/email', icon: Mail },
    { label: 'nav.automations', path: '/marketing/automations', icon: Zap },
    { label: 'nav.funnels', path: '/funnels', icon: Megaphone },
    { label: 'nav.formationBundles', path: '/marketing/formation-bundles', icon: Layers },
    { label: 'nav.sellerPayments', path: '/marketing/payments', icon: CreditCard },
    { label: 'nav.siteWeb', path: '/marketing/site', icon: Globe },
    { label: 'nav.blog', path: '/marketing/site/blog', icon: Newspaper },
    { label: 'nav.creatorPage', path: '/marketing/creator-page', icon: LinkIcon },
    { label: 'nav.crm', path: '/marketing/crm', icon: Kanban },
    { label: 'nav.affiliate', path: '/marketing/affiliate', icon: Share2 },
    { label: 'nav.promotions', path: '/marketing/promotions', icon: Tag },
    { label: 'nav.booking', path: '/marketing/booking', icon: Calendar },
    { label: 'nav.communities', path: '/marketing/communities', icon: MessageCircle },
    { label: 'nav.webinarsEvergreen', path: '/marketing/webinars', icon: Presentation },
    { label: 'nav.physicalProducts', path: '/marketing/physical-products', icon: Package },
    { label: 'nav.smsMarketing', path: '/marketing/sms', icon: Smartphone },
  ],
};

const LYRRAONE_TEACHER_NAV_SECTIONS: NavSection[] = [
  {
    key: 'pilotage',
    label: 'nav.section.pilotage',
    items: [
      { label: 'nav.home', path: '/dashboard', icon: Home },
      { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
      { label: 'nav.analytics', path: '/analytics', icon: BarChart3 },
    ],
  },
  {
    key: 'activite',
    label: 'nav.section.activity',
    items: [],
  },
  {
    key: 'medias',
    label: 'nav.section.medias',
    items: [
      { label: 'nav.videos', path: '/videos', icon: Video },
      { label: 'nav.sounds', path: '/sounds', icon: Music },
      { label: 'nav.images', path: '/images', icon: Image },
      { label: 'nav.documents', path: '/documents', icon: FileText },
      { label: 'nav.mediaDeposits', path: '/media/deposits', icon: Share2 },
    ],
  },
  {
    key: 'aide',
    label: 'nav.section.aide',
    items: [{ label: 'nav.help', path: '/help', icon: HelpCircle }],
  },
];

const LYRRAONE_EDITOR_NAV_SECTIONS: NavSection[] = [
  {
    key: 'pilotage',
    label: 'nav.section.pilotage',
    items: [{ label: 'nav.home', path: '/dashboard', icon: Home }],
  },
  {
    key: 'activite',
    label: 'nav.section.activity',
    items: [],
  },
  {
    key: 'medias',
    label: 'nav.section.medias',
    items: [
      { label: 'nav.videos', path: '/videos', icon: Video },
      { label: 'nav.sounds', path: '/sounds', icon: Music },
      { label: 'nav.images', path: '/images', icon: Image },
      { label: 'nav.documents', path: '/documents', icon: FileText },
      { label: 'nav.mediaDeposits', path: '/media/deposits', icon: Share2 },
    ],
  },
  {
    key: 'aide',
    label: 'nav.section.aide',
    items: [{ label: 'nav.help', path: '/help', icon: HelpCircle }],
  },
];

const TEACHER_NAV_SECTIONS: NavSection[] = [
  {
    key: 'pilotage',
    label: 'nav.section.pilotage',
    items: [
      { label: 'nav.home', path: '/dashboard', icon: Home },
      { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
      { label: 'nav.learners', path: '/learners', icon: Users },
      { label: 'nav.analytics', path: '/analytics', icon: BarChart3 },
      { label: 'nav.producerAssignments', path: '/producer-assignments', icon: Sparkles, requiresInstitution: true },
    ],
  },
  {
    key: 'creation',
    label: 'nav.section.creation',
    items: [
      { label: 'nav.eduflowList', path: '/eduflow/list', icon: Workflow },
      { label: 'nav.podcasts', path: '/podcasts', icon: Podcast },
    ],
  },
  {
    key: 'medias',
    label: 'nav.section.medias',
    items: [
      { label: 'nav.videos', path: '/videos', icon: Video },
      { label: 'nav.sounds', path: '/sounds', icon: Music },
      { label: 'nav.images', path: '/images', icon: Image },
      { label: 'nav.documents', path: '/documents', icon: FileText },
      { label: 'nav.mediaDeposits', path: '/media/deposits', icon: Share2 },
    ],
  },
  {
    key: 'aide',
    label: 'nav.section.aide',
    items: [
      { label: 'nav.help', path: '/help', icon: HelpCircle },
    ],
  },
];
const TEACHER_NAV_ITEMS: NavItem[] = TEACHER_NAV_SECTIONS.flatMap((section) => section.items);

/** Espace éditeur : création + médias, sans apprenants, analytics, webinaires ni espace apprenant. */
const EDITOR_NAV_SECTIONS: NavSection[] = [
  {
    key: 'pilotage',
    label: 'nav.section.pilotage',
    items: [{ label: 'nav.home', path: '/dashboard', icon: Home }],
  },
  {
    key: 'creation',
    label: 'nav.section.creation',
    items: [
      { label: 'nav.eduflowList', path: '/eduflow/list', icon: Workflow },
      { label: 'nav.podcasts', path: '/podcasts', icon: Podcast },
    ],
  },
  {
    key: 'medias',
    label: 'nav.section.medias',
    items: [
      { label: 'nav.videos', path: '/videos', icon: Video },
      { label: 'nav.sounds', path: '/sounds', icon: Music },
      { label: 'nav.images', path: '/images', icon: Image },
      { label: 'nav.documents', path: '/documents', icon: FileText },
      { label: 'nav.mediaDeposits', path: '/media/deposits', icon: Share2 },
    ],
  },
  {
    key: 'aide',
    label: 'nav.section.aide',
    items: [{ label: 'nav.help', path: '/help', icon: HelpCircle }],
  },
];

const NAV_CONFIG: Record<string, NavItem[]> = {
  editor: EDITOR_NAV_SECTIONS.flatMap((section) => section.items),
  institution: [
    { label: 'nav.institutionPanel', path: '/dashboard', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
  ],
  institution_admin: [
    { label: 'nav.institutionPanel', path: '/dashboard', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
  ],
  admin: [
    { label: 'nav.adminPanel', path: '/admin', icon: Shield },
  ],
  drh: [
    { label: 'nav.institutionPanel', path: '/dashboard', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
  ],
  rh: [
    { label: 'nav.institutionPanel', path: '/dashboard', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
    { label: 'nav.integrations', path: '/integrations', icon: Plug, institutionMenuFlag: INSTITUTION_MENU_FLAGS.integrations_page },
  ],
  resp_formation: [
    { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
    { label: 'nav.institutionPanel', path: '/dashboard', icon: Building2, institutionMenuFlag: INSTITUTION_MENU_FLAGS.institution_panel },
  ],
  manager: [
    { label: 'nav.dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'nav.myTeam', path: '/team', icon: Users },
    { label: 'nav.reviews', path: '/reviews', icon: Star },
  ],
  collaborator: [
    { label: 'nav.myTraining', path: '/training', icon: GraduationCap },
    { label: 'nav.mySkills', path: '/skills', icon: Target },
    { label: 'nav.certificates', path: '/certificates', icon: Award },
  ],
  learner: [
    { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
  ],
  student: [
    { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
  ],
};

function NavTooltip({ label, show }: { label: string; show: boolean }) {
  if (!show) return null;
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-lg ltr:start-full ltr:ms-2 rtl:end-full rtl:me-2 dark:bg-gray-700"
    >
      {label}
    </div>
  );
}

function navPathMatchesLocation(path: string, loc: { pathname: string; search: string }): boolean {
  const have = new URLSearchParams(loc.search);

  if (path.includes('?')) {
    const [p, q] = path.split('?', 2);
    // Les onglets dashboard (?tab=) ne vivent que sur la pathname exacte.
    if (loc.pathname !== p) return false;
    const want = new URLSearchParams(q);
    for (const [k, v] of want.entries()) {
      if (have.get(k) !== v) return false;
    }
    return true;
  }

  if (loc.pathname !== path && !loc.pathname.startsWith(`${path}/`)) {
    return false;
  }

  /** Accueil dashboard : racine /dashboard uniquement, sans onglet institution. */
  if (path === '/dashboard') {
    if (loc.pathname !== '/dashboard') return false;
    const tab = have.get('tab');
    return !tab || tab === 'home';
  }

  if (path === '/settings') {
    return loc.pathname === '/settings';
  }

  /** Hub marketing : racine uniquement, pas les sous-modules. */
  if (path === '/marketing') {
    return loc.pathname === '/marketing';
  }

  return true;
}

/** Routes hors sidebar qui doivent surligner un onglet dashboard LyrraOne. */
function lyrraOneRelatedNavPath(loc: { pathname: string; search: string }): string | null {
  if (!isLyrraOne()) return null;
  const { pathname } = loc;
  if (/^\/eduflow(?:\/|$)/.test(pathname)) return '/dashboard?tab=flows';
  if (/^\/podcasts(?:\/|$)/.test(pathname)) return '/dashboard?tab=supports';
  if (pathname === '/dashboard/learners' || pathname.startsWith('/dashboard/learners/')) {
    return '/dashboard?tab=learners';
  }
  return null;
}

/** Un seul item actif : le match le plus spécifique gagne. */
function resolveExclusiveActiveNavPath(
  items: NavItem[],
  loc: { pathname: string; search: string },
): string | null {
  const related = lyrraOneRelatedNavPath(loc);
  if (related && items.some((i) => i.path === related)) return related;

  const matches = items.filter((item) => navPathMatchesLocation(item.path, loc));
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0].path;

  const have = new URLSearchParams(loc.search);
  const exact = matches.find((item) => {
    if (!item.path.includes('?')) {
      return loc.pathname === item.path && !loc.search;
    }
    const [p, q] = item.path.split('?', 2);
    if (loc.pathname !== p) return false;
    const want = new URLSearchParams(q);
    for (const [k, v] of want.entries()) {
      if (have.get(k) !== v) return false;
    }
    return true;
  });
  if (exact) return exact.path;

  return matches.sort((a, b) => b.path.length - a.path.length)[0]?.path ?? null;
}

export function Sidebar() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const fetchProfile = useAuthStore((s) => s.fetchProfile);
  const { sidebarCollapsed, setSidebarCollapsed } = useUIStore();
  const { institutionLogoSrc, institutionLogoSmallSrc } = useInstitutionAppTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const role = (user?.role ?? 'teacher') as UserRole;
  const logoHomePath =
    isLyrraOne() && isMobile && canAccessTrainerDashboard(role)
      ? MOBILE_TRAINER_SALES_PATH
      : '/dashboard';
  const menuFlags = user?.institutionDashboardMenuFlags;

  const hasInstitution = !!user?.institutionId;
  const { planUsage } = useLyrraOnePlan();
  const loTeam = user?.loTeam;

  const navItems = useMemo(() => {
    const raw = NAV_CONFIG[role] ?? TEACHER_NAV_ITEMS;
    if (!Array.isArray(raw)) return raw;
    return raw.filter(
      (item) =>
        (!item.institutionMenuFlag || isInstitutionMenuEnabled(menuFlags, item.institutionMenuFlag)) &&
        (!item.requiresInstitution || hasInstitution),
    );
  }, [role, menuFlags, hasInstitution]);

  const navSections = useMemo(() => {
    const filterMarketingItems = (items: NavItem[]): NavItem[] => {
      if (!isLyrraOne()) return items;
      return items.filter((item) => isMarketingPathAllowed(item.path, planUsage));
    };

    const filterTeamPermissions = (sections: NavSection[]): NavSection[] => {
      if (!isLyrraOne() || !loTeam || loTeam.isOwner) return sections;
      return sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => canAccessLoTeamPath(item.path, loTeam)),
        }))
        .filter((section) => section.items.length > 0);
    };

    const injectLyrraOneOperationalNav = (sections: NavSection[]): NavSection[] => {
      if (!isLyrraOne()) return sections;
      const ops = buildFormateurOperationalNavItems(t, menuFlags ?? undefined, hasInstitution).filter((item) => {
        const tabMatch = item.path.match(/[?&]tab=([^&]+)/);
        if (!tabMatch) return true;
        const tabId = tabMatch[1];
        if (!isValidInstitutionTab(tabId)) return true;
        return isInstitutionTabAllowed(tabId, planUsage);
      });
      if (ops.length === 0) return sections;
      const activiteIdx = sections.findIndex((s) => s.key === 'activite');
      if (activiteIdx < 0) return sections;
      return sections.map((section, idx) =>
        idx === activiteIdx
          ? {
              ...section,
              items: ops.map((item) => ({
                label: item.labelKey,
                path: item.path,
                icon: item.icon,
              })),
            }
          : section,
      );
    };

    const injectFormateurInstitutionNav = (sections: NavSection[]): NavSection[] => {
      if (isLyrraOne()) return injectLyrraOneOperationalNav(sections);

      const instItems = buildFormateurInstitutionNavItems(t, menuFlags ?? undefined, hasInstitution);
      if (instItems.length === 0) return sections;

      if (sections.some((s) => s.key === 'institution')) return sections;
      const institutionSection: NavSection = {
        key: 'institution',
        label: 'nav.section.institution',
        items: instItems.map((item) => ({
          label: item.labelKey,
          path: item.path,
          icon: item.icon,
        })),
      };
      const marketingIdx = sections.findIndex((s) => s.key === 'marketing');
      const insertAt = marketingIdx >= 0 ? marketingIdx : sections.length;
      return [...sections.slice(0, insertAt), institutionSection, ...sections.slice(insertAt)];
    };

    const injectLyrraOneMarketing = (sections: NavSection[]): NavSection[] => {
      if (!isLyrraOne()) return sections;
      const hasMarketing = sections.some((s) => s.key === 'marketing');
      const marketingSection: NavSection = {
        ...LYRRAONE_MARKETING_SECTION,
        items: filterMarketingItems(LYRRAONE_MARKETING_SECTION.items),
      };
      if (hasMarketing) {
        return sections.map((s) =>
          s.key === 'marketing' ? { ...s, items: filterMarketingItems(s.items) } : s,
        );
      }
      const activiteIdx = sections.findIndex((s) => s.key === 'activite');
      const insertAt = activiteIdx >= 0 ? activiteIdx + 1 : sections.length;
      return [
        ...sections.slice(0, insertAt),
        marketingSection,
        ...sections.slice(insertAt),
      ];
    };

    const filterLyrraOneNav = (sections: NavSection[]): NavSection[] => {
      if (!isLyrraOne() || !isMobile || !canAccessTrainerDashboard(role)) return sections;
      return [
        {
          key: 'mobile-trainer',
          label: 'nav.section.activity',
          items: [
            { label: 'institution.tabs.sales', path: MOBILE_TRAINER_SALES_PATH, icon: CreditCard },
            { label: 'nav.myLearning', path: '/learner', icon: GraduationCap },
          ],
        },
      ];
    };

    if (roleQualifiesForEditorCreatorNav(role)) {
      const base = isLyrraOne() ? LYRRAONE_EDITOR_NAV_SECTIONS : EDITOR_NAV_SECTIONS;
      return filterTeamPermissions(
        filterLyrraOneNav(injectFormateurInstitutionNav(injectLyrraOneMarketing(base))).filter(
          (section) => section.items.length > 0,
        ),
      );
    }
    if (roleQualifiesForTrainerWebinarUi(role)) {
      if (isLyrraOne()) {
        return filterTeamPermissions(
          filterLyrraOneNav(
            injectFormateurInstitutionNav(injectLyrraOneMarketing(LYRRAONE_TEACHER_NAV_SECTIONS)),
          ).filter((section) => section.items.length > 0),
        );
      }
      return filterTeamPermissions(filterLyrraOneNav(injectFormateurInstitutionNav(injectLyrraOneMarketing(TEACHER_NAV_SECTIONS.map((section) => {
        if (section.key !== 'creation') {
          return {
            ...section,
            items: section.items.filter(
              (item) =>
                (!(item as NavItem).institutionMenuFlag ||
                  isInstitutionMenuEnabled(menuFlags, (item as NavItem).institutionMenuFlag!)) &&
                (!(item as NavItem).requiresInstitution || hasInstitution),
            ),
          };
        }
        const extra: NavItem[] = hasInstitution ? [{ label: 'nav.webinars', path: '/webinars', icon: Presentation }] : [];
        const merged = [...section.items, ...extra];
        return {
          ...section,
          items: merged.filter(
            (item) =>
              !item.institutionMenuFlag || isInstitutionMenuEnabled(menuFlags, item.institutionMenuFlag),
          ),
        };
      })))));
    }
    if (['institution', 'institution_admin', 'drh', 'rh', 'resp_formation'].includes(role)) {
      if (isLyrraOne()) {
        const base = LYRRAONE_TEACHER_NAV_SECTIONS.map((section) =>
          section.key === 'pilotage'
            ? {
                ...section,
                items: navItems.filter(
                  (item) =>
                    (!item.institutionMenuFlag ||
                      isInstitutionMenuEnabled(menuFlags, item.institutionMenuFlag)) &&
                    (!item.requiresInstitution || hasInstitution),
                ),
              }
            : section,
        );
        return filterTeamPermissions(
          filterLyrraOneNav(injectFormateurInstitutionNav(injectLyrraOneMarketing(base))).filter(
            (section) => section.items.length > 0,
          ),
        );
      }
      const baseSections: NavSection[] = [
        {
          key: 'pilotage',
          label: 'nav.section.pilotage',
          items: navItems.filter(
            (item) =>
              (!item.institutionMenuFlag || isInstitutionMenuEnabled(menuFlags, item.institutionMenuFlag)) &&
              (!item.requiresInstitution || hasInstitution),
          ),
        },
      ];
      return filterTeamPermissions(filterLyrraOneNav(injectFormateurInstitutionNav(injectLyrraOneMarketing(baseSections))));
    }
    return filterTeamPermissions([{ key: 'default', label: '', items: navItems }]);
  }, [role, navItems, menuFlags, hasInstitution, t, isMobile, planUsage, loTeam]);

  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);
  const showSettingsEntry =
    !isMobile &&
    isLyrraOne() &&
    (roleQualifiesForEditorCreatorNav(role) ||
      roleQualifiesForTrainerWebinarUi(role) ||
      ['institution', 'institution_admin', 'drh', 'rh', 'resp_formation'].includes(role)) &&
    isLoTeamPermissionAllowed(loTeam?.menuPermissions, 'nav.settings', loTeam?.isOwner !== false);

  const allNavItems = useMemo(() => navSections.flatMap((section) => section.items), [navSections]);
  const activeNavPath = useMemo(
    () => resolveExclusiveActiveNavPath(allNavItems, location),
    [allNavItems, location],
  );
  const settingsActive = navPathMatchesLocation('/settings', location);
  const canSwitchEnvironment = Boolean(isLyrraOne() && user?.loTeam?.canSwitchEnvironment);
  const activeWorkspaceMode = (user?.loTeam?.mode ?? 'own') as LoWorkspaceMode;
  const workspaceEnvironments = user?.loTeam?.environments ?? [];

  const switchWorkspace = async (mode: LoWorkspaceMode) => {
    if (mode === activeWorkspaceMode) return;
    setLoWorkspaceMode(mode);
    try {
      await fetchProfile();
      navigate('/dashboard', { replace: true });
    } catch {
      /* profil rechargé au prochain appel API */
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <motion.div
      className="flex h-full flex-col overflow-hidden border-e border-[color:var(--inst-player-border,#d8e0ec)] bg-[color:var(--inst-player-sidebar-panel-bg,var(--inst-player-surface,#ffffff))] dark:border-[hsl(217,33%,22%)] dark:bg-[color:var(--inst-player-sidebar-panel-bg,hsl(217,33%,12%))]"
      animate={{ width: sidebarCollapsed ? 64 : 240 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      {/* Logo */}
      <div
        data-sidebar-logo
        className="flex h-16 flex-shrink-0 items-center border-b border-[color:var(--inst-player-border,#d8e0ec)] bg-[color:var(--inst-player-surface,#ffffff)] px-4 dark:border-[hsl(217,33%,22%)] dark:bg-[color:var(--inst-player-surface,hsl(217,33%,17%))]"
      >
        <Link
          to={logoHomePath}
          className={cn(
            'flex cursor-pointer items-center overflow-hidden',
            !isLyrraOne() && institutionLogoSrc
              ? cn('w-full px-0', sidebarCollapsed ? 'justify-center' : 'justify-start')
              : 'gap-2',
          )}
        >
            {isLyrraOne() ? (
            <LyrraOneLogo
              variant={sidebarCollapsed ? 'icon' : 'full'}
              size={sidebarCollapsed ? 'sm' : 'md'}
              linkTo={false}
            />
          ) : institutionLogoSrc ? (
            <img
              src={sidebarCollapsed && institutionLogoSmallSrc ? institutionLogoSmallSrc : institutionLogoSrc}
              alt={t('sidebar.logoInstitutionAlt')}
              className={cn(
                'h-10 max-h-11 w-auto max-w-[min(100%,168px)] flex-shrink-0 object-contain',
                sidebarCollapsed ? 'h-8 max-h-8 max-w-[44px] object-center' : 'object-start',
              )}
            />
          ) : (
            <>
              <img src="/LogoSmall.png" alt={t('sidebar.logoAlt', { appName: getAppName(), defaultValue: `${getAppName()} — Go to home` })} className="h-8 w-8 flex-shrink-0 rounded-lg dark:hidden" />
              <img src="/LogoLightSmall.png" alt={t('sidebar.logoAlt', { appName: getAppName(), defaultValue: `${getAppName()} — Go to home` })} className="hidden h-8 w-8 flex-shrink-0 rounded-lg dark:block" />
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="flex flex-col overflow-hidden leading-tight whitespace-nowrap"
                  >
                    <span className="text-lg font-bold text-slate-900 dark:text-white">{getAppName()}</span>
                    <span className="text-[10px] font-medium uppercase tracking-widest text-[color:var(--inst-player-text-muted,#64748b)] dark:text-[#B6CAEC]">
                      {t('sidebar.brandTagline')}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav
        className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-3 scrollbar-thin"
        aria-label={t('nav.sidebarNavigation', 'Main navigation')}
        data-tour="tour-sidebar-nav"
      >
        {navSections.map((section) => (
          <div key={section.key} className="space-y-1">
            {!sidebarCollapsed && section.label && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--inst-player-text-muted,#64748b)] dark:text-[hsl(215,25%,72%)]">
                {t(section.label)}
              </p>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const translatedLabel = t(item.label);
              const isItemActive = activeNavPath === item.path;

              return (
                <div
                  key={`${item.path}-${item.label}`}
                  className="relative"
                  onMouseEnter={() => sidebarCollapsed && setHoveredItem(item.path)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Link
                    to={item.path}
                    aria-current={isItemActive ? 'page' : undefined}
                    data-nav-active={isItemActive ? 'true' : undefined}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                      isItemActive
                        ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_18%,transparent)] dark:text-white'
                        : 'text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[color:var(--inst-player-surface-muted,#e4ebf4)] dark:hover:bg-gray-700/50 hover:text-[#0C1C38] dark:hover:text-gray-200',
                    )}
                  >
                    <>
                      <Icon
                        className={cn(
                          'h-5 w-5 flex-shrink-0 transition-colors',
                          isItemActive ? 'text-[color:var(--inst-player-primary,#0C1C38)] dark:text-white' : '',
                        )}
                      />
                      <AnimatePresence>
                        {!sidebarCollapsed && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="truncate"
                          >
                            {translatedLabel}
                          </motion.span>
                        )}
                      </AnimatePresence>
                      {isItemActive && (
                        <motion.div
                          layoutId="sidebar-active-indicator"
                          className="absolute bottom-2 start-0 top-2 w-1 rounded-e-full bg-[color:var(--inst-player-primary,#0C1C38)]"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}
                    </>
                  </Link>
                  <NavTooltip label={translatedLabel as string} show={sidebarCollapsed && hoveredItem === item.path} />
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="flex-shrink-0 space-y-1 border-t border-[color:var(--inst-player-border,#e5e7eb)] p-2 dark:border-gray-700">
        {showSettingsEntry ? (
          <div
            className="relative"
            onMouseEnter={() => sidebarCollapsed && setHoveredItem('/settings')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <Link
              to="/settings"
              aria-current={settingsActive ? 'page' : undefined}
              data-nav-active={settingsActive ? 'true' : undefined}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                settingsActive
                  ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_18%,transparent)] dark:text-white'
                  : 'text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F0F4FA] dark:hover:bg-gray-700/50 hover:text-[#0C1C38] dark:hover:text-gray-200',
              )}
            >
              <Settings className="h-5 w-5 flex-shrink-0" />
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="truncate"
                  >
                    {t('nav.settings', 'Paramètres')}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
            <NavTooltip
              label={t('nav.settings', 'Paramètres') as string}
              show={sidebarCollapsed && hoveredItem === '/settings'}
            />
          </div>
        ) : null}
        {canSwitchEnvironment ? (
          <div className="space-y-1">
            {!sidebarCollapsed ? (
              <p className="px-3 pt-1 text-[10px] font-semibold uppercase tracking-wide text-[#8A94A6] dark:text-gray-500">
                Environnement
              </p>
            ) : null}
            {workspaceEnvironments.map((env) => {
              const isActive = env.mode === activeWorkspaceMode;
              return (
                <div
                  key={env.mode}
                  className="relative"
                  onMouseEnter={() => sidebarCollapsed && setHoveredItem(`workspace:${env.mode}`)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <button
                    type="button"
                    onClick={() => void switchWorkspace(env.mode)}
                    aria-pressed={isActive}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                      isActive
                        ? 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-[color:color-mix(in_srgb,var(--inst-player-accent,#5eead4)_18%,transparent)] dark:text-white'
                        : 'text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F0F4FA] dark:hover:bg-gray-700/50 hover:text-[#0C1C38] dark:hover:text-gray-200',
                    )}
                  >
                    <ArrowLeftRight className="h-5 w-5 flex-shrink-0" aria-hidden />
                    <AnimatePresence>
                      {!sidebarCollapsed && (
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="truncate text-start"
                        >
                          {env.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                  <NavTooltip
                    label={env.label}
                    show={sidebarCollapsed && hoveredItem === `workspace:${env.mode}`}
                  />
                </div>
              );
            })}
          </div>
        ) : null}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-[#5B6472] dark:text-[#B6CAEC] hover:bg-[#F0F4FA] dark:hover:bg-gray-700/50 hover:text-[#0C1C38] dark:hover:text-gray-200 transition-colors"
          aria-label={sidebarCollapsed ? t('sidebar.expandAria') : t('sidebar.collapseAria')}
        >
          {sidebarCollapsed ? (
            <ChevronsRight className={cn('h-5 w-5 flex-shrink-0', rtlMirrorHorizontalChevron)} />
          ) : (
            <>
              <ChevronsLeft className={cn('h-5 w-5 flex-shrink-0', rtlMirrorHorizontalChevron)} />
              <span className="truncate">{t('sidebar.collapse')}</span>
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

export default Sidebar;
